import React, { useEffect, useMemo, useRef, useState } from 'react'
import { NumericFormat } from 'react-number-format'
import { Col, Row } from 'react-grid-system'
import { DateInput, Input, Textarea, Toggle } from '../../common/form/fields'
import {
   useNoteMutations,
   useForm,
   useSettings,
   useAttachments,
   useNoteDetails,
} from '../../../hooks'

import * as yup from 'yup'
import * as noteStyles from './notes.module.scss'
import { Modal, Button, LoadingOverlay, SnackBar } from '../../common'

import NoteAttachmentPicker from './noteAttachmentPicker'
import { useMediaQuery } from 'react-responsive'
import { WarningModal } from '../../common/warningModal/warningModal'
import { FileAttachment, NoteModalProps } from './types'
import { ToolNotePermission, Severity } from '../../../types'
import { defaultNote, requiredErrorMsg } from './constants'
import { parseDate } from '../../../utils/datetime'

export const NoteModal = ({
   noteId,
   itemDetails,
   isModalOpen,
   setIsModalOpen,
   onSaveNote,
   isReadOnly,
}: NoteModalProps) => {
   const { settings } = useSettings()

   const { note, getNote, isLoading } = useNoteDetails()

   const {
      upload,
      attachmentData,
      isUploading,
      isUploadError,
      isUploadSuccess,
      removeAttachment,
      isDeleteAttachmentPending,
      isDeleteSuccess,
   } = useAttachments()

   const { update, isSuccessUpdate, isErrorUpdate, isUpdating } =
      useNoteMutations()

   const isMobileLayout = useMediaQuery({ query: '(max-width: 767px)' })
   const attachmentRef = useRef<HTMLInputElement | null>(null)

   const [selectedNote, setSelectedNote] = useState<any>(null)
   const [noteCompleted, setNoteCompleted] = useState<boolean>(
      selectedNote?.Completed ?? false
   )
   const [isAttachmentWarningModalOpen, setIsAttachmentWarningModalOpen] =
      useState<boolean>(false)
   const [importFiles, setImportFiles] = useState<FileList | undefined>()
   const [attachments, setAttachments] = useState<FileAttachment[]>([])
   const [attachmentToRemove, setAttachmentToRemove] =
      useState<FileAttachment | null>(null)
   const [noteTextValue, setNoteTextValue] = useState<string>(
      selectedNote?.NoteText
   )
   const [snackBarMessage, setSnackBarMessage] = useState<string>('')
   const [snackBarSeverity, setSnackBarSeverity] = useState<Severity | null>(
      null
   )

   const noteSchema = yup.object().shape({
      ToolId: yup
         .string()
         .typeError(requiredErrorMsg)
         .required(requiredErrorMsg),
      NoteTypeId: yup
         .string()
         .typeError(requiredErrorMsg)
         .required(requiredErrorMsg),
      CompletedDate: noteCompleted
         ? yup.date().typeError(requiredErrorMsg).required(requiredErrorMsg)
         : null,
      CompletedBy: noteCompleted
         ? yup.string().typeError(requiredErrorMsg).required(requiredErrorMsg)
         : null,
   })

   useEffect(() => {
      if (noteId) {
         getNote(noteId)
      }
   }, [noteId])

   useEffect(() => {
      if (note) {
         setSelectedNote(note)
         setNoteTextValue(note?.NoteText)
         setNoteCompleted(note?.Completed)
         setAttachments(note?.Attachments)
      }
   }, [note])

   useEffect(() => {
      if (attachmentData?.length > 0) {
         setAttachments((prevState) => [...prevState, ...attachmentData])
      }
   }, [attachmentData])

   useEffect(() => {
      if (importFiles) {
         // remove unwantedimports
         const importFilesArray = Array.from(importFiles).filter(
            (file) =>
               file.type.includes('image') ||
               file.name.includes('.doc') ||
               file.name.includes('.docx') ||
               file.name.includes('.pdf')
         )

         const formData = new FormData()
         importFilesArray.map((file, index) => {
            formData.append(`file[${index}]`, file)
         }) // @ts-ignore
         upload(formData)
      }
   }, [importFiles])

   const prepareNoteForApi = (values) => {
      const attachmentsToAdd = attachments.map((attachment) => attachment.Id)
      return {
         ...values,
         Odometer: parseFloat(values?.Odometer.replace(/,/g, '')),
         Hours: parseFloat(values?.Hours.replace(/,/g, '')),
         ...(values?.CompletedCost && {
            CompletedCost: parseFloat(values?.CompletedCost.replace(/,/g, '')),
         }), // conditionally add completedCost if note completed and submitted
         Attachments: attachmentsToAdd,
      }
   }

   const { onSubmit, validationErrors, onReset } = useForm(
      noteSchema,
      (values) => update(prepareNoteForApi(values))
   )

   useEffect(() => {
      if (isSuccessUpdate) {
         setSnackBarMessage('The Note was updated')
         setSnackBarSeverity(Severity.SUCCESS)
         onSaveNote()
      }
   }, [isSuccessUpdate])

   useEffect(() => {
      if (isErrorUpdate) {
         setSnackBarMessage('Error updating the Note')
         setSnackBarSeverity(Severity.ERROR)
         resetAndCloseModal()
      }
   }, [isErrorUpdate])

   useEffect(() => {
      if (isUploadSuccess) {
         setSnackBarMessage('The file was uploaded')
         setSnackBarSeverity(Severity.SUCCESS)
      }
   }, [isUploadSuccess])

   useEffect(() => {
      if (isUploadError) {
         setSnackBarMessage('Error uploading file')
         setSnackBarSeverity(Severity.ERROR)
      }
   }, [isUploadError])

   useEffect(() => {
      if (isDeleteSuccess) {
         setSnackBarMessage('The attachment was removed')
         setSnackBarSeverity(Severity.SUCCESS)
      }
   }, [isDeleteSuccess])

   const resetAndCloseModal = () => {
      setSelectedNote(defaultNote)
      setIsModalOpen(false)
   }

   const handleDateChange = (name: string, date: Date) => {
      setSelectedNote({ ...selectedNote, [name]: date })
   }

   const onNoteTextChange = (e) => setNoteTextValue(e.target.value)

   const handleCompletedToggle = (_id: string, checked: boolean) => {
      setNoteCompleted(checked)
      setSelectedNote({
         ...selectedNote,
         CompletedDate: !noteCompleted ? new Date() : '',
      })
   }

   const handleClose = () => {
      onReset()
      resetAndCloseModal()
   }

   const handleAddAttachment = () => {
      attachmentRef.current.click()
   }

   const handleRemoveAttachment = (attachment: FileAttachment) => {
      removeAttachment({ attachmentId: attachment.Id })
      setAttachments(attachments.filter((file) => file !== attachment))
   }

   const handleCheckRemoveAttachment = (e: any, attachment: FileAttachment) => {
      e.preventDefault()
      if (selectedNote?.ToolNotePermission <= 1) return // tool permission view cannot delete attachment
      setAttachmentToRemove(attachment)
      setIsAttachmentWarningModalOpen(true)
   }

   const handleConfirmRemoveAttachment = () => {
      handleRemoveAttachment(attachmentToRemove)
      setIsAttachmentWarningModalOpen(false)
      setAttachmentToRemove(null)
   }

   const dateOptions = { day: 'numeric', month: 'short', year: 'numeric' }
   const noteDate = new Date(selectedNote?.CreatedOn)

   const showLoadingSpinner = useMemo(
      () => isLoading || isUpdating,
      [isLoading, isUpdating]
   )

   const modalTitle = useMemo(
      () =>
         isReadOnly ||
         selectedNote?.ToolNotePermission === ToolNotePermission.View
            ? 'View Note'
            : 'Edit Note',
      [isReadOnly, selectedNote]
   )

   const displayAttachments = useMemo(() => {
      if (
         selectedNote?.ToolNotePermission === ToolNotePermission.View &&
         !selectedNote?.Attachments?.length
      ) {
         // user can only view and no attachments don't show attachment picker
         return false
      }
      if (isReadOnly && !selectedNote?.Attachments?.length) {
         // if viewing from actions and no notes attached
         return false
      }
      if (
         selectedNote?.ToolNotePermission === ToolNotePermission.View &&
         selectedNote?.Attachments?.length
      ) {
         // if user can only view and attachments present can view and download
         return true
      }
      return true
   }, [selectedNote, isReadOnly])

   const showSnackbar = useMemo(
      () =>
         isUploadError ||
         isUploadSuccess ||
         isErrorUpdate ||
         isSuccessUpdate ||
         isDeleteSuccess,
      [
         isUploadError,
         isUploadSuccess,
         isErrorUpdate,
         isSuccessUpdate,
         isDeleteSuccess,
      ]
   )

   return (
      <>
         {showLoadingSpinner && <LoadingOverlay />}
         <Modal
            isModalVisible={isModalOpen}
            closeModal={handleClose}
            title={modalTitle}
            marginTop={isMobileLayout && '2rem'}
            zIndex={'500'}
            stickyHeader
         >
            <form
               id="note-modal-form"
               className={noteStyles.form}
               noValidate
               onSubmit={onSubmit}
            >
               <div
                  style={{
                     pointerEvents:
                        selectedNote?.ToolNotePermission <=
                           ToolNotePermission.View || isReadOnly
                           ? 'none'
                           : 'auto',
                  }}
               >
                  <input type="hidden" name="Id" value={selectedNote?.Id} />
                  <input type="hidden" name="ToolId" value={itemDetails?.ID} />
                  {itemDetails?.ToolLoanId && (
                     <input
                        type="hidden"
                        name="ToolLoanId"
                        value={itemDetails?.ToolLoanId}
                     />
                  )}
                  <input
                     type="hidden"
                     name="NoteTypeId"
                     value={selectedNote?.NoteTypeId}
                  />
                  <input
                     type="hidden"
                     name="Completed"
                     value={noteCompleted?.toString()}
                  />
                  <input
                     type="file"
                     hidden
                     id="import"
                     onChange={(event) => setImportFiles(event?.target?.files)}
                     accept="image/*, .doc, .docx, .pdf"
                     multiple={true}
                     ref={attachmentRef}
                  />
                  <Row gutterWidth={20}>
                     <Col xs={12} md={6}>
                        <div className={noteStyles.noteInfoLeft}>
                           <h4 className={noteStyles.semiBold}>
                              {itemDetails?.Title}
                           </h4>
                           <h5 className={noteStyles.semiBold}>
                              {selectedNote?.NoteTypeTitle}
                           </h5>
                           <div className={noteStyles.infoText}>
                              {`Added by ${
                                 selectedNote?.CreatedByName ?? ''
                              } on ${noteDate.toLocaleDateString(
                                 'en-GB',
                                 // @ts-ignore
                                 dateOptions
                              )}`}
                           </div>
                        </div>
                     </Col>
                     <Col xs={12} md={6}>
                        <div className={noteStyles.noteInfoRight}>
                           <div className={noteStyles.infoText}>
                              {`Serial # ${
                                 itemDetails?.SerialNumber
                                    ? itemDetails.SerialNumber
                                    : '-'
                              }`}
                           </div>
                           <div className={noteStyles.infoText}>
                              {`Barcode ${
                                 itemDetails?.Barcode
                                    ? itemDetails.Barcode
                                    : '-'
                              }`}
                           </div>
                           {settings.CustomText1Enabled &&
                              itemDetails?.CustomText1Value && (
                                 <div className={noteStyles.infoText}>
                                    {`${settings?.CustomText1Label} ${itemDetails?.CustomText1Value}`}
                                 </div>
                              )}
                        </div>
                     </Col>
                  </Row>
                  <Textarea
                     id="NoteText"
                     height={180}
                     label="Note"
                     value={noteTextValue}
                     onChange={onNoteTextChange}
                  />

                  <Row gutterWidth={20}>
                     <Col xs={12} md={4}>
                        <DateInput
                           id="DueDate"
                           label="Due Date"
                           popperPlacement="top-start"
                           description={
                              !selectedNote?.DueDate &&
                              'Send a Notification on this Date'
                           }
                           insetDescription={true}
                           onChange={(date) =>
                              handleDateChange('DueDate', date)
                           }
                           selectedDate={parseDate(selectedNote?.DueDate)}
                        />
                     </Col>
                     <Col xs={12} md={4}>
                        <NumericFormat
                           customInput={Input}
                           id="Odometer"
                           label="Odometer"
                           value={selectedNote?.Odometer}
                           maxLength={'11'}
                           thousandSeparator=","
                           allowNegative={false}
                           decimalScale={0}
                        />
                     </Col>
                     <Col xs={12} md={4}>
                        <NumericFormat
                           customInput={Input}
                           id="Hours"
                           label="Hours"
                           value={selectedNote?.Hours}
                           decimalScale={2}
                           thousandSeparator=","
                           allowNegative={false}
                           maxLength={'10'}
                        />
                     </Col>
                  </Row>
                  <Row gutterWidth={20}>
                     <Col xs={12} md={1.5} className={noteStyles.toggleColumn}>
                        <div className={noteStyles.toggle}>
                           <div className={noteStyles.toggleField}>
                              <Toggle
                                 id="Completed"
                                 size="md"
                                 isChecked={noteCompleted}
                                 onToggle={handleCompletedToggle}
                              />
                           </div>
                           <div className={noteStyles.toggleLabel}>
                              Completed
                           </div>
                        </div>
                     </Col>
                     {noteCompleted && (
                        <div
                           className={
                              !isMobileLayout
                                 ? noteStyles.completedInputWrapper
                                 : noteStyles.completedInputWrapperMobile
                           }
                        >
                           <Col xs={12} md={4}>
                              <Input
                                 id="CompletedBy"
                                 label="Completed By"
                                 value={selectedNote?.CompletedBy}
                                 error={validationErrors?.CompletedBy}
                              />
                           </Col>
                           <Col xs={12} md={4}>
                              <DateInput
                                 id="CompletedDate"
                                 label="Completed Date"
                                 popperPlacement="top-start"
                                 withWrappers={true}
                                 onChange={(date) =>
                                    handleDateChange('CompletedDate', date)
                                 }
                                 selectedDate={parseDate(
                                    selectedNote?.CompletedDate
                                 )}
                                 error={validationErrors?.CompletedDate}
                              />
                           </Col>
                           <Col xs={12} md={4}>
                              <div className={noteStyles.currency}>
                                 <span />
                                 <NumericFormat
                                    customInput={Input}
                                    id="CompletedCost"
                                    label="Completed Cost"
                                    value={selectedNote?.CompletedCost}
                                    decimalScale={2}
                                    thousandSeparator=","
                                    allowNegative={false}
                                    maxLength={'11'}
                                 />
                              </div>
                           </Col>
                        </div>
                     )}
                  </Row>
               </div>
               {displayAttachments && (
                  <NoteAttachmentPicker
                     attachments={attachments}
                     isUploading={isUploading}
                     isDeleteAttachmentPending={isDeleteAttachmentPending}
                     handleAddAttachment={handleAddAttachment}
                     handleCheckRemoveAttachment={handleCheckRemoveAttachment}
                     selectedNotePermission={selectedNote?.ToolNotePermission}
                     selectedNoteHasId={!!selectedNote?.Id}
                     isReadonly={isReadOnly}
                  />
               )}
               {modalTitle === 'Edit Note' && (
                  <Row gutterWidth={20}>
                     <Col xs={6}>
                        <Button
                           type="submit"
                           form="note-modal-form"
                           minWidth="100%"
                           disabled={isUploading}
                        >
                           Save
                        </Button>
                     </Col>
                     <Col xs={6}>
                        <Button
                           minWidth="100%"
                           variant="tertiary"
                           onClick={handleClose}
                           isReset
                        >
                           Cancel
                        </Button>
                     </Col>
                  </Row>
               )}
            </form>
            {modalTitle === 'View Note' && (
               <form className={noteStyles.form}>
                  <Row gutterWidth={20}>
                     <Col xs={12}>
                        <Button
                           minWidth="100%"
                           variant="tertiary"
                           onClick={handleClose}
                           isReset
                        >
                           Close
                        </Button>
                     </Col>
                  </Row>
               </form>
            )}
         </Modal>
         {isAttachmentWarningModalOpen && (
            <WarningModal
               isModalOpen={isAttachmentWarningModalOpen}
               handleClose={() => setIsAttachmentWarningModalOpen(false)}
               title="Remove Attachment"
               message="Are you sure?"
               primaryButtonTitle="Yes"
               primaryButtonMethod={handleConfirmRemoveAttachment}
               primaryButtonVariant="danger"
               secondaryButtonTitle="No"
               secondaryButtonMethod={() =>
                  setIsAttachmentWarningModalOpen(false)
               }
               secondaryButtonVariant="tertiary"
               zIndex="500"
            />
         )}
         {showSnackbar && (
            <SnackBar
               message={snackBarMessage}
               open={showSnackbar}
               severity={snackBarSeverity}
               disableAutoClose={false}
            />
         )}
      </>
   )
}
